
import React, { useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useBooking } from '@/contexts/BookingContext';
import Header from '@/components/sections/Header';
import Footer from '@/components/sections/Footer';
import HeroSection from '@/components/sections/HeroSection';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import StructureSection from '@/components/sections/StructureSection';
import VideoSection from '@/components/sections/VideoSection';
import AboutSection from '@/components/sections/AboutSection';
import BlogSection from '@/components/sections/BlogSection';
import LocationSection from '@/components/sections/LocationSection';
import Breadcrumbs from '@/components/Breadcrumbs';
import jornada01 from '@/assets/journey/primeiro-contato-consultorio.webp';
import jornada02 from '@/assets/journey/consulta-escuta-cirurgiao-plastico.webp';
import jornada03 from '@/assets/journey/avaliacao-clinica-pre-cirurgia.webp';
import jornada04 from '@/assets/journey/planejamento-cirurgico-personalizado.webp';
import jornada05 from '@/assets/journey/preparacao-pre-operatoria.webp';
import jornada06 from '@/assets/journey/acompanhamento-nutrologico-integrado.webp';
import jornada07 from '@/assets/dia-cirurgia-ambiente-hospitalar.webp';
import jornada08 from '@/assets/journey/acompanhamento-pos-operatorio.webp';

const journeySteps = [
  { number: "01", subtitle: "Primeiro contato", title: "Atendimento inicial e orientação", description: "O primeiro contato geralmente acontece pelo WhatsApp ou pelos canais digitais do consultório. Nesse momento, minha equipe orienta sobre o agendamento da consulta e esclarece dúvidas iniciais sobre o processo.\n\nDesde o início, buscamos garantir que você se sinta bem informada e segura para dar o primeiro passo.", image: jornada01 },
  { number: "02", subtitle: "Consulta e escuta atenta", title: "Entendendo seus objetivos", description: "Durante a consulta, conversamos sobre o que motivou sua busca pela cirurgia. Esse é um momento importante para entender suas expectativas, seu histórico e o que você espera alcançar com o procedimento.\n\nMeu objetivo é compreender seu caso de forma completa antes de qualquer decisão.", image: jornada02 },
  { number: "03", subtitle: "Avaliação clínica detalhada", title: "Análise do corpo e das proporções", description: "Realizo uma avaliação física cuidadosa para analisar proporções corporais, qualidade da pele e características anatômicas relevantes para o procedimento.\n\nEssa análise permite definir quais abordagens cirúrgicas são mais adequadas para o seu caso.", image: jornada03 },
  { number: "04", subtitle: "Planejamento cirúrgico personalizado", title: "Definição da estratégia cirúrgica", description: "Com base na avaliação clínica, elaboro um planejamento cirúrgico individualizado. Explico de forma clara as técnicas indicadas, as possibilidades de resultado e os cuidados necessários no processo.\n\nA decisão é sempre tomada com critério técnico e alinhamento de expectativas.", image: jornada04 },
  { number: "05", subtitle: "Preparação pré-operatória", title: "Exames e orientações importantes", description: "Antes da cirurgia, são solicitados exames e orientações específicas para garantir segurança durante o procedimento. Quando necessário, ajustes na rotina ou na alimentação podem ser recomendados.\n\nEssa etapa é fundamental para que o procedimento aconteça dentro dos protocolos médicos adequados.", image: jornada05 },
  { number: "06", subtitle: "Acompanhamento nutrológico integrado", title: "Dra. Giovanna Spagnuolo", description: "Antes de qualquer procedimento, o corpo precisa estar preparado. Por meio de uma avaliação completa — que inclui estado nutricional, vitaminas, minerais, saúde intestinal e inflamação metabólica — é estruturado um plano individualizado com foco em cicatrização e redução de riscos.\n\nEsse cuidado integrado permite uma cirurgia mais segura, um pós-operatório mais rápido e um resultado final superior.", image: jornada06, objectPosition: "center 25%" },
  { number: "07", subtitle: "Dia da cirurgia", title: "Procedimento realizado em ambiente hospitalar", description: "A cirurgia é realizada em hospital, com equipe médica e anestésica especializada. Cada etapa do procedimento segue protocolos de segurança e planejamento previamente definidos.\n\nO objetivo é realizar a cirurgia com precisão técnica e cuidado em todos os detalhes.", image: jornada07 },
  { number: "08", subtitle: "Acompanhamento pós-operatório", title: "Recuperação com acompanhamento próximo", description: "Após a cirurgia, acompanho de perto a evolução da recuperação. Oriento cada etapa do pós-operatório e permaneço disponível para esclarecer dúvidas durante esse período.\n\nEsse acompanhamento é essencial para garantir uma recuperação segura e uma boa evolução do resultado ao longo do tempo.", image: jornada08 },
];

const JornadaPage: React.FC = () => {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const { handleWhatsAppOpen } = useBooking();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-index'));
          if (entry.isIntersecting) {
            setVisibleItems((prev) => prev.includes(index) ? prev : [...prev, index]);
          }
        });
      },
      { threshold: 0.2, rootMargin: '-50px 0px' }
    );
    itemRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-dark-base">
      <Helmet>
        <title>Jornada do Paciente | Dr. Luis Brunello em Curitiba</title>
        <meta name="description" content="Conheça cada etapa da jornada cirúrgica com o Dr. Luis Brunello: da consulta inicial ao pós-operatório. Transparência e cuidado em cada fase." />
        <link rel="canonical" href="https://luisbrunello.com.br/jornada" />
        <meta property="og:title" content="Jornada do Paciente | Dr. Luis Brunello em Curitiba" />
        <meta property="og:description" content="Conheça cada etapa da jornada cirúrgica com o Dr. Luis Brunello: da consulta inicial ao pós-operatório. Transparência e cuidado em cada fase." />
        <meta property="og:url" content="https://luisbrunello.com.br/jornada" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Dr. Luis Brunello" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:image" content="https://luisbrunello.com.br/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Jornada do Paciente | Dr. Luis Brunello – Cirurgia Plástica em Curitiba" />
        <meta name="twitter:description" content="Conheça cada etapa da jornada cirúrgica com o Dr. Luis Brunello: da consulta inicial ao pós-operatório. Transparência e cuidado em cada fase." />
        <meta name="twitter:image" content="https://luisbrunello.com.br/og-image.jpg" />
      </Helmet>
      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={() => {}}
        onOpenBooking={handleWhatsAppOpen}
      />

      <main>
        {/* ── Hero Banner ── */}
        <HeroSection
          scrollToSection={(id) => {
            const target = id === 'sobre' ? 'jornada-timeline' : id;
            document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          onOpenBooking={handleWhatsAppOpen}
          overrideTitle={<>Sua jornada com o <span className="text-gradient">Dr. Luis Brunello</span></>}
          overrideSubtitle="Dr. Luis Brunello • Cirurgião plástico em Curitiba"
          overrideDescription={"Acompanho você desde a primeira conversa até a finalização do resultado. Explico tudo com clareza e mantenho um pós-operatório próximo, sempre atento à evolução individual."}
        />

        {/* ── Timeline ── */}
        <section id="jornada-timeline" aria-label="Etapas da jornada do paciente" className="section-spacing bg-dark-base relative overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] decorative-blur decorative-blur-primary opacity-15 pointer-events-none" aria-hidden="true" />
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <Breadcrumbs
              items={[
                { label: 'Home', href: '/' },
                { label: 'Jornada do Paciente' },
              ]}
              className="mb-10 max-w-6xl mx-auto"
            />
            <div className="max-w-6xl mx-auto relative">
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-white/30 to-transparent transform -translate-x-1/2 z-0" aria-hidden="true" />
              <div className="md:hidden absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-white/5 via-white/20 to-white/5" aria-hidden="true" />

              <ol className="space-y-12 md:space-y-24 list-none p-0 m-0">
                {journeySteps.map((step, index) => {
                  const isVisible = visibleItems.includes(index);
                  const isEven = index % 2 === 0;
                  return (
                    <li
                      key={index}
                      ref={(el) => (itemRefs.current[index] = el)}
                      data-index={index}
                      className={`list-none relative transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                    >
                      <div className="hidden md:flex absolute left-1/2 top-8 transform -translate-x-1/2 z-10" aria-hidden="true">
                        <div className={`w-14 h-14 rounded-full bg-dark-surface border border-white/10 flex items-center justify-center text-light-primary font-semibold text-sm shadow-glow transition-all duration-500 ${isVisible ? 'scale-100' : 'scale-0'}`}>
                          {step.number}
                        </div>
                      </div>
                      <div className="md:hidden absolute left-6 top-0 transform -translate-x-1/2 z-10" aria-hidden="true">
                        <div className={`w-12 h-12 rounded-full bg-dark-surface border border-white/10 flex items-center justify-center text-light-primary font-semibold text-xs shadow-glow transition-all duration-500 ${isVisible ? 'scale-100' : 'scale-0'}`}>
                          {step.number}
                        </div>
                      </div>
                      <article className="grid md:grid-cols-2 gap-8 md:gap-12 items-center pl-14 md:pl-0">
                        <div className={`${isEven ? 'md:pr-16 md:text-right' : 'md:order-2 md:pl-16'}`}>
                          <p className="subtitle-wide text-gold">
                            <span className="sr-only">Etapa {step.number}: </span>{step.subtitle}
                          </p>
                          <h2 className="text-2xl md:text-3xl text-light-primary mt-2 mb-4 leading-tight tracking-tight">
                            {step.title}
                          </h2>
                          <div className="space-y-3 text-light-muted leading-relaxed text-base md:text-lg">
                            {step.description.split('\n\n').map((para, i) => (
                              <p key={i}>{para}</p>
                            ))}
                          </div>
                        </div>
                        <figure className={`${isEven ? 'md:order-2 md:pl-16' : 'md:pr-16'} m-0`}>
                          <div className={`overflow-hidden rounded-2xl shadow-dark-xl transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                            <img
                              src={step.image}
                              alt={step.subtitle}
                              className="w-full h-64 md:h-80 object-cover bg-dark-surface"
                              style={step.objectPosition ? { objectPosition: step.objectPosition } : undefined}
                              loading="lazy"
                              decoding="async"
                              width="560"
                              height="320"
                            />
                          </div>
                        </figure>
                      </article>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-20 text-center">
                <div className="inline-block glass-card px-8 py-6 rounded-2xl">
                  <h3 className="text-xl md:text-2xl text-light-primary mb-2">
                    Com você até o fim da recuperação
                  </h3>
                  <p className="text-light-muted text-sm md:text-base max-w-2xl">
                    Meu compromisso é com a sua segurança e com o resultado — presente em cada fase, do primeiro contato até o fim do acompanhamento pós-operatório.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Seções compartilhadas ── */}
        <TestimonialsSection />
        <StructureSection />
        <VideoSection />
        <AboutSection onOpenBooking={handleWhatsAppOpen} />
        <BlogSection />
        <LocationSection onOpenBooking={handleWhatsAppOpen} />
      </main>

      <Footer />
    </div>
  );
};

export default JornadaPage;
